import React from "react";
import { Link } from "react-router-dom";
import { Plus, Star } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { Button } from "../ui/button";
import { toast } from "sonner";

import { trackEvent, toGaItem } from "../../lib/analyticsEvents";

export const ProductCard = ({ product, index = 0 }) => {
	const { addItem } = useCart();

	const formatPrice = (price) => {
		return new Intl.NumberFormat("fr-CH", {
			style: "currency",
			currency: "CHF",
		}).format(price);
	};

	const handleAddToCart = (e) => {
		e.preventDefault();
		e.stopPropagation();

		addItem(product, 1);

		trackEvent("add_to_cart", {
			currency: "CHF",
			value: Number(product.price || 0),
			items: [toGaItem(product, 1)],
		});

		toast.success(`${product.name} ajouté au panier`, {
			description: formatPrice(product.price),
			duration: 2000,
		});
	};

	const handleSelectItem = () => {
		trackEvent("select_item", {
			item_list_id: "menu",
			item_list_name: "Menu",
			items: [toGaItem(product, 1)],
		});
	};

	const animationDelay = (index % 6) * 0.1;

	return (
		<article
			data-testid={`product-card-${product.id}`}
			className="card-product opacity-0 animate-fade-in"
			style={{ animationDelay: `${animationDelay}s` }}
		>
			<Link
				to={`/produit/${product.slug}`}
				className="block"
				onClick={handleSelectItem}
			>
				{/* Image Container */}
				<div className="relative aspect-[4/5] bg-warm-secondary overflow-hidden mb-4">
					<img
						src={product.image_url}
						alt={product.name}
						loading="lazy"
						className="w-full h-full object-cover"
					/>

					{/* Badges */}
					{product.featured && (
						<span className="absolute top-4 left-4 bg-latte-gold text-warm-alabaster px-3 py-1 text-xs font-medium uppercase tracking-wider">
							Vedette
						</span>
					)}

					{product.tags?.includes("nouveau") && !product.featured && (
						<span className="absolute top-4 left-4 bg-espresso text-warm-alabaster px-3 py-1 text-xs font-medium uppercase tracking-wider">
							Nouveau
						</span>
					)}

					{/* Quick Add Button */}
					<Button
						onClick={handleAddToCart}
						data-testid={`add-to-cart-${product.id}`}
						className="absolute bottom-4 right-4 w-10 h-10 p-0 bg-warm-alabaster text-espresso hover:bg-espresso hover:text-warm-alabaster opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-full shadow-lg"
					>
						<Plus className="w-5 h-5" />
					</Button>
				</div>

				{/* Content */}
				<div className="space-y-2">
					{/* Category */}
					<span className="text-xs uppercase tracking-widest text-latte-gold font-medium">
						{product.category === "cafes" && "Café"}
						{product.category === "boissons-froides" && "Boisson froide"}
						{product.category === "patisseries" && "Pâtisserie"}
						{product.category === "snacks" && "Snack"}
						{product.category === "saisonniers" && "Saisonnier"}
					</span>

					{/* Name */}
					<h3 className="font-serif text-lg font-semibold text-espresso group-hover:text-latte-gold transition-colors duration-300">
						{product.name}
					</h3>

					{/* Description */}
					<p className="text-soft-charcoal-secondary text-sm line-clamp-2">
						{product.short_description}
					</p>

					{/* Price & Action */}
					<div className="flex items-center justify-between pt-2">
						<span className="font-serif text-lg font-semibold text-espresso">
							{formatPrice(product.price)}
						</span>

						{!product.available && (
							<span className="text-xs text-red-500 font-medium uppercase">
								Épuisé
							</span>
						)}
					</div>
				</div>
			</Link>
		</article>
	);
};

export default ProductCard;
